import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth";
import images from "../images";
import styles from "./adminSidebar.module.scss";
import { APP_VERSION } from "../version";
import {
  LuLayoutDashboard,
  LuShoppingCart,
  LuPackage,
  LuStore,
  LuLink2,
  LuMegaphone,
  LuHeadphones,
  LuChevronDown,
  LuChevronRight,
  LuPanelLeftClose,
  LuPanelLeftOpen,
} from "react-icons/lu";

const NAV_ITEMS = [
  {
    title: "Dashboard",
    icon: LuLayoutDashboard,
    path: "/admin-panel/Dashboard",
  },
  {
    title: "Vendas",
    icon: LuShoppingCart,
    children: [
      { title: "Vendas", path: "/admin-panel/Invoices" },
      { title: "Venda Presencial", path: "/admin-panel/venda-balcao" },
      { title: "Cupons", path: "/admin-panel/coupon-add" },
      { title: "Venda Link", path: "/admin-panel/payment-links" },
    ],
  },
  {
    title: "Produtos",
    icon: LuPackage,
    children: [
      { title: "Produtos", path: "/admin-panel/Product" },
      { title: "Adicionar Produto", path: "/admin-panel/product-create" },
      { title: "Custos", path: "/admin-panel/costs" },
      { title: "Precificação em massa", path: "/admin-panel/bulk-pricing" },
    ],
  },
  {
    title: "Estoque",
    icon: LuStore,
    children: [
      { title: "Estoques", path: "/admin-panel/Stocks" },
      { title: "Avaliações", path: "/admin-panel/reviews" },
      { title: "Contagem de Estoque", path: "/admin-panel/StockCount" },
    ],
  },
  {
    title: "Integrações",
    icon: LuLink2,
    children: [
      { title: "Bling (NF-e)", path: "/admin-panel/BlingIntegration" },
      { title: "TikTok Shop", path: "/admin-panel/TikTokIntegration" },
      { title: "Sincronizar Google", path: "/admin-panel/GoogleIntegration" },
      {
        title: "Sincronizar Facebook",
        path: "/admin-panel/FacebookIntegration",
      },
    ],
  },
  {
    title: "Marketing",
    icon: LuMegaphone,
    children: [
      { title: "Campanhas WhatsApp", path: "/admin-panel/marketing-campanhas" },
    ],
  },
  {
    title: "Atendimento",
    icon: LuHeadphones,
    children: [
      { title: "Conversas", path: "/admin-panel/atendimento-conversas" },
    ],
  },
];

// `collapsed` (só desktop): menu recolhido mostra só os ícones, com o nome
// no tooltip. Clicar num grupo recolhido abre o menu já com o grupo aberto.
export default function AdminSidebar({
  isOpen,
  onClose,
  collapsed = false,
  onToggleCollapsed,
}) {
  const { user } = useAuth();
  const location = useLocation();

  const [openMenus, setOpenMenus] = useState(() => {
    const initial = {};
    NAV_ITEMS.forEach((item, idx) => {
      if (item.children?.some((c) => location.pathname.startsWith(c.path))) {
        initial[idx] = true;
      }
    });
    return initial;
  });

  const toggleMenu = (idx) => {
    if (collapsed) {
      setOpenMenus((prev) => ({ ...prev, [idx]: true }));
      onToggleCollapsed && onToggleCollapsed();
      return;
    }
    setOpenMenus((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");
  const isParentActive = (item) => item.children?.some((c) => isActive(c.path));

  const initial = user?.email?.[0]?.toUpperCase() || "A";

  return (
    <>
      {isOpen && <div className={styles.backdrop} onClick={onClose} />}
      <aside
        className={`${styles.sidebar} ${isOpen ? styles.open : ""} ${
          collapsed ? styles.collapsed : ""
        }`}
      >
        <div className={styles.logo}>
          <Link
            to="/admin-panel/Dashboard"
            onClick={onClose}
            className={styles.logoLink}
          >
            <img src={images.svelta} alt="Svelta" />
          </Link>
          {onToggleCollapsed && (
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
              title={collapsed ? "Expandir menu" : "Recolher menu"}
            >
              {collapsed ? (
                <LuPanelLeftOpen size={18} />
              ) : (
                <LuPanelLeftClose size={18} />
              )}
            </button>
          )}
        </div>

        <nav className={styles.nav}>
          {NAV_ITEMS.map((item, idx) => {
            const Icon = item.icon;
            const active = item.path
              ? isActive(item.path)
              : isParentActive(item);
            const isExpanded = openMenus[idx];

            if (item.path) {
              return (
                <Link
                  key={idx}
                  to={item.path}
                  className={`${styles.navItem} ${active ? styles.active : ""}`}
                  onClick={onClose}
                  title={collapsed ? item.title : undefined}
                >
                  <Icon size={17} />
                  <span>{item.title}</span>
                </Link>
              );
            }

            return (
              <div key={idx} className={styles.navGroup}>
                <button
                  className={`${styles.navItem} ${active ? styles.activeParent : ""}`}
                  onClick={() => toggleMenu(idx)}
                  title={collapsed ? item.title : undefined}
                >
                  <Icon size={17} />
                  <span>{item.title}</span>
                  <span className={styles.chevron}>
                    {isExpanded ? (
                      <LuChevronDown size={13} />
                    ) : (
                      <LuChevronRight size={13} />
                    )}
                  </span>
                </button>
                {isExpanded && !collapsed && (
                  <div className={styles.subMenu}>
                    {item.children.map((child, cidx) => (
                      <Link
                        key={cidx}
                        to={child.path}
                        className={`${styles.subItem} ${
                          isActive(child.path) ? styles.subActive : ""
                        }`}
                        onClick={onClose}
                      >
                        {child.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.version}>v{APP_VERSION}</div>

        <div
          className={styles.footer}
          title={collapsed ? user?.email : undefined}
        >
          <div className={styles.avatar}>{initial}</div>
          <div className={styles.userDetails}>
            <span className={styles.userName}>
              {user?.displayName || "Admin"}
            </span>
            <span className={styles.userEmail}>{user?.email}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
